import React, { Component } from "react";

const RELOAD_GUARD_KEY = "chunkReloadedAt";
const RELOAD_GUARD_MS = 10000;

const isChunkError = error => {
  const msg = (error && error.message) || "";
  return (
    (error && error.name === "ChunkLoadError") ||
    /Loading (CSS )?chunk|Loading chunk [\d]+ failed|dynamically imported module|Failed to fetch dynamically imported module/i.test(
      msg
    )
  );
};

// Allow a hard reload unless we already forced one in the last few seconds —
// that stops a reload loop when the new build genuinely isn't on the server yet
// (e.g. an upload still in progress). The key auto-expires, so no cleanup needed.
const reloadAllowed = () => {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_GUARD_KEY));
    return !at || Date.now() - at > RELOAD_GUARD_MS;
  } catch (e) {
    return true;
  }
};

class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, reloading: false };
  }

  static getDerivedStateFromError(error) {
    if (isChunkError(error) && reloadAllowed()) {
      return { failed: true, reloading: true };
    }
    return { failed: true, reloading: false };
  }

  componentDidCatch(error) {
    if (this.state.reloading && isChunkError(error)) {
      try {
        sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
      } catch (e) {
        /* sessionStorage unavailable — reload anyway */
      }
      window.location.reload();
    }
  }

  render() {
    // Stale chunk — almost always a fresh deploy that replaced the old files.
    // Hard-reload to pull the new index.html + hashed bundles, and render
    // nothing in the meantime so the user never sees an error flash.
    if (this.state.reloading) {
      return null;
    }

    if (this.state.failed) {
      return (
        <div style={{
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          height: "100%", minHeight: "60vh", gap: 16, padding: "2rem",
          textAlign: "center",
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
            stroke="#dd4498" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="1" y1="1" x2="23" y2="23"/>
            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/>
            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/>
            <path d="M10.71 5.05A16 16 0 0 1 22.56 9"/>
            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/>
            <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
            <line x1="12" y1="20" x2="12.01" y2="20"/>
          </svg>
          <p style={{ fontSize: 16, color: "#555", margin: 0 }}>
            This page couldn't load.<br />
            Check your connection and refresh.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "#dd4498", color: "#fff", border: "none",
              borderRadius: 8, padding: "10px 28px", fontSize: 15,
              fontWeight: 600, cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
